import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { settingsService } from '../../../lib/services';

const DEFAULT_INTEGRATIONS = [
  { id: 'google_analytics', name: 'Google Analytics', description: 'Track website traffic and user behavior', icon: 'BarChart3', settingKey: 'integration_google_analytics_id', webhookKey: null },
  { id: 'google_tag_manager', name: 'Google Tag Manager', description: 'Manage marketing tags without code changes', icon: 'Tag', settingKey: 'integration_gtm_id', webhookKey: null },
  { id: 'mailchimp', name: 'Mailchimp', description: 'Email marketing and automation platform', icon: 'Mail', settingKey: 'integration_mailchimp_key', webhookKey: null },
  { id: 'stripe', name: 'Stripe', description: 'Payment processing and subscription management', icon: 'CreditCard', settingKey: 'integration_stripe_key', webhookKey: null },
  { id: 'slack', name: 'Slack', description: 'Team notifications and collaboration', icon: 'MessageSquare', settingKey: 'integration_slack_webhook', webhookKey: null },
  { id: 'zapier', name: 'Zapier', description: 'Connect with 5000+ apps and automate workflows', icon: 'Zap', settingKey: 'integration_zapier_key', webhookKey: null },
];

const IntegrationSettings = () => {
  const [integrationValues, setIntegrationValues] = useState({});
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState({ contentPublished: false, userCreated: false, pageUpdated: false, seoChanges: false });
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showKeyFor, setShowKeyFor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const { data, error } = await settingsService?.getByCategory('integrations');
        if (!error && data) {
          const vals = {};
          data?.forEach(s => {
            vals[s?.settingKey] = s?.settingValue || '';
          });
          setIntegrationValues(vals);
          setWebhookUrl(vals?.['integration_webhook_url'] || '');
          try {
            const events = JSON.parse(vals?.['integration_webhook_events'] || '{}');
            setWebhookEvents(prev => ({ ...prev, ...events }));
          } catch { /* ignore */ }
        }
      } catch (err) {
        console.error('Failed to load integration settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleConnect = (integration) => {
    setEditingId(integration?.id);
    setEditValue(integrationValues?.[integration?.settingKey] || '');
  };

  const handleSaveKey = async (integration) => {
    const updated = { ...integrationValues, [integration?.settingKey]: editValue };
    setIntegrationValues(updated);
    setEditingId(null);
    await settingsService?.upsert([{
      settingKey: integration?.settingKey,
      settingValue: editValue,
      settingType: 'string',
      category: 'integrations'
    }]);
  };

  const handleDisconnect = async (integration) => {
    const updated = { ...integrationValues, [integration?.settingKey]: '' };
    setIntegrationValues(updated);
    await settingsService?.upsert([{
      settingKey: integration?.settingKey,
      settingValue: '',
      settingType: 'string',
      category: 'integrations'
    }]);
  };

  const handleSaveWebhook = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const settings = [
        { settingKey: 'integration_webhook_url', settingValue: webhookUrl, settingType: 'string', category: 'integrations' },
        { settingKey: 'integration_webhook_events', settingValue: JSON.stringify(webhookEvents), settingType: 'json', category: 'integrations' },
      ];
      const { error } = await settingsService?.upsert(settings);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save webhook settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
        <span className="text-muted-foreground">Loading integration settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <Icon name="CheckCircle" size={16} />
          Integration settings saved successfully!
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-5">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">Integration Security</h4>
            <p className="text-xs md:text-sm text-muted-foreground">
              All API keys and credentials are encrypted and stored securely. Never share your API keys publicly.
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {DEFAULT_INTEGRATIONS?.map((integration) => {
          const isConnected = !!(integrationValues?.[integration?.settingKey]);
          const isEditing = editingId === integration?.id;
          return (
            <div key={integration?.id} className="bg-card rounded-lg border border-border p-4 md:p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isConnected ? 'bg-success/10' : 'bg-muted'
                  }`}>
                    <Icon
                      name={integration?.icon}
                      size={20}
                      className={isConnected ? 'text-success' : 'text-muted-foreground'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">{integration?.name}</h3>
                    <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">{integration?.description}</p>
                  </div>
                </div>
                <div className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                  isConnected ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                }`}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </div>
              </div>
              {isConnected && !isEditing && (
                <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs md:text-sm text-muted-foreground">API Key / ID</span>
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => setShowKeyFor(showKeyFor === integration?.id ? null : integration?.id)}
                    >
                      {showKeyFor === integration?.id ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className="text-xs md:text-sm font-mono text-foreground truncate">
                    {showKeyFor === integration?.id
                      ? integrationValues?.[integration?.settingKey]
                      : '••••••••••••••••'}
                  </div>
                </div>
              )}
              {isEditing && (
                <div className="mb-4 space-y-2">
                  <Input
                    type="text"
                    placeholder={`Enter ${integration?.name} API key or ID`}
                    value={editValue}
                    onChange={(e) => setEditValue(e?.target?.value)}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" variant="default" onClick={() => handleSaveKey(integration)} className="flex-1">
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {!isEditing && (
                <div className="flex flex-col sm:flex-row gap-2">
                  {isConnected ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Edit2"
                        iconPosition="left"
                        onClick={() => handleConnect(integration)}
                        className="flex-1"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        iconName="Unplug"
                        iconPosition="left"
                        onClick={() => handleDisconnect(integration)}
                        className="flex-1"
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Link"
                      iconPosition="left"
                      onClick={() => handleConnect(integration)}
                      fullWidth
                    >
                      Connect
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Webhook" size={20} className="text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Webhook Configuration</h3>
            <p className="text-sm text-muted-foreground">Configure webhooks for real-time event notifications</p>
          </div>
        </div>

        <div className="space-y-4">
          <Input
            label="Webhook URL"
            type="url"
            placeholder="https://your-domain.com/webhook"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e?.target?.value)}
            description="Receive POST requests when events occur"
          />

          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Event Types</label>
            <Checkbox
              label="Content Published"
              checked={webhookEvents?.contentPublished}
              onChange={(e) => setWebhookEvents(prev => ({ ...prev, contentPublished: e?.target?.checked }))}
            />
            <Checkbox
              label="User Created"
              checked={webhookEvents?.userCreated}
              onChange={(e) => setWebhookEvents(prev => ({ ...prev, userCreated: e?.target?.checked }))}
            />
            <Checkbox
              label="Page Updated"
              checked={webhookEvents?.pageUpdated}
              onChange={(e) => setWebhookEvents(prev => ({ ...prev, pageUpdated: e?.target?.checked }))}
            />
            <Checkbox
              label="SEO Changes"
              checked={webhookEvents?.seoChanges}
              onChange={(e) => setWebhookEvents(prev => ({ ...prev, seoChanges: e?.target?.checked }))}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="default" size="sm" iconName="Save" iconPosition="left" loading={isSaving} onClick={handleSaveWebhook}>
              Save Webhook
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSettings;